import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";

import { selectIdentity } from "../../../selectors/userSelectors";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import NumberedPagination from "../../../shared/ui/Pagination/NumberedPagination";
import Field from "../../../shared/ui/Field/Field";
import TextInput from "../../../shared/ui/Field/TextInput";
import InlineError from "../../../shared/ui/Field/InlineError";
import { messageFromError } from "../../../shared/api/drfErrors";
import {
  fetchCompany,
  fetchMembers,
  fetchMyCompany,
} from "../../companyWorkspace/api/companyWorkspace";
import { isAdminMembership } from "../../companyWorkspace/lib/companyLabels";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
} from "../../companyWorkspace/lib/scopeSession";
import { useCompanyFocusRevalidation } from "../../companyWorkspace/lib/companyFocusRevalidation";
import CompanyPageHeader from "../../companyWorkspace/ui/CompanyPageHeader";
import { isCorporateClientsAvailable } from "../lib/clientLabels";
import {
  activeMemberOptions,
  allowedValuesFor,
  canonicalChoiceText,
  classificationLabel,
  classificationTone,
  committableCounts,
  createIdempotencyKey,
  diagnosticCodeText,
  diagnosticText,
  explicitMappingFromSuggestions,
  fieldKindLabel,
  formatLabel,
  importFieldLabel,
  missingMappingText,
  fileContentToken,
  importRequestFingerprint,
  isInterruptedOperation,
  isZeroRowCompleted,
  missingRequiredMappings,
  operationStateLabel,
  operationStateTone,
  outcomeLabel,
  outcomeTone,
  presentApiError,
  textError,
  inspectWarningText,
  readOperationId,
  requirementsClaimBlocked,
  resolveCommitKey,
  sheetSelectionRequired,
  sourceNamespaceMissing,
  sourceNamespacePrompt,
  unresolvedHeaders,
} from "../lib/clientImportPresentation";
import {
  commitClientImport,
  fetchClientImportOperation,
  fetchClientImportOperationRows,
  inspectClientImport,
  previewClientImport,
} from "../api/clientImport";

const ROW_PAGE_SIZE = 20;
const ROW_RESULTS_UNAVAILABLE =
  "Імпорт прийнято. Результати рядків не завантажились. Оновіть сторінку, щоб відкрити збережену операцію.";
const INTERRUPTED_COPY =
  "Операцію перервано. Вона не завершена і може бути продовжена тим самим запитом. Браузер не зберігає файл джерела. Після перезавантаження продовження потребує повторного вибору того самого файлу і тих самих параметрів. Сервер тоді пропустить уже збережені результати.";
const CROSS_ACTOR_RESUME_COPY =
  "Продовжити цей імпорт може лише користувач, який його розпочав.";
const RELOAD_RESUME_COPY =
  "Оригінальний файл не зберігається. Оберіть його знову і підтвердіть зіставлення та параметри. Продовження надішле початковий ключ ідемпотентності.";
const NAMESPACE_REQUIRED_COPY =
  "Укажіть простір джерела. Порожнє або пробільне значення не приймається.";

function durableOperationId(payload) {
  const value = Number(payload && payload.id);
  if (!Number.isInteger(value) || value <= 0) return null;
  return value;
}

function errorView(err) {
  const data = err && err.response && err.response.data;
  const code = data && typeof data === "object" && data.code ? String(data.code) : "";
  return {
    status: err && err.response ? err.response.status : null,
    code,
    detail: messageFromError(err),
  };
}

function integerUserMap(mapping) {
  const next = {};
  Object.keys(mapping || {}).forEach((key) => {
    const userId = Number(mapping[key]);
    if (Number.isInteger(userId) && userId > 0) next[key] = userId;
  });
  return next;
}

function CompanyClientImportContent() {
  const { companyId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const busyRef = useRef(false);
  const commitKeyRef = useRef(null);
  const pinnedResumeKeyRef = useRef(null);
  const fileSelectionRef = useRef(0);
  const fetchedReceiptRef = useRef(null);
  const fileInputRef = useRef(null);
  const stepsRef = useRef(null);

  const [company, setCompany] = useState(null);
  const [membership, setMembership] = useState(null);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [state, setState] = useState("loading");
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [fileToken, setFileToken] = useState("");
  const [inspect, setInspect] = useState(null);
  const [sheet, setSheet] = useState("");
  const [sheetExplicit, setSheetExplicit] = useState(false);
  const [mapping, setMapping] = useState({});
  const [mappingConfirmed, setMappingConfirmed] = useState(false);
  const [valueMappings, setValueMappings] = useState({});
  const [responsibleMapping, setResponsibleMapping] = useState({});
  const [sourceNamespace, setSourceNamespace] = useState("");
  const [members, setMembers] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewFingerprint, setPreviewFingerprint] = useState("");
  const [operation, setOperation] = useState(null);
  const [rowPage, setRowPage] = useState(null);
  const [commitAttempt, setCommitAttempt] = useState(null);
  const [receiptArm, setReceiptArm] = useState(null);
  const [receiptGeneration, setReceiptGeneration] = useState(0);
  const [resumeMode, setResumeMode] = useState(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [busy, setBusy] = useState(false);

  const companyMatches = company != null && sameCompanyId(company.id, companyId);
  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);
  const authorityMatches = companyMatches && actorMatches;
  const canImport =
    authorityMatches &&
    isAdminMembership(company, membership) &&
    isCorporateClientsAvailable(company);

  const operationId = readOperationId(location.search);
  const fields = inspect?.canonical_fields || [];
  const fingerprint = useMemo(
    () =>
      importRequestFingerprint({
        fileToken,
        sheet,
        mapping,
        valueMappings,
        responsibleMapping,
        sourceNamespace,
      }),
    [fileToken, sheet, mapping, valueMappings, responsibleMapping, sourceNamespace],
  );
  const previewMatches = Boolean(preview) && previewFingerprint === fingerprint;
  const counts = committableCounts(preview?.summary);
  const namespaceNeeded = sourceNamespacePrompt(mapping);
  const namespaceMissing = sourceNamespaceMissing(mapping, sourceNamespace);
  const actorCanResume =
    operation != null &&
    isInterruptedOperation(operation.state) &&
    sameActorId(operation.actor_id, actorId) &&
    Boolean(operation.idempotency_key);
  const sameSessionContinuation =
    resumeMode === "session" &&
    Boolean(file) &&
    operation != null &&
    isInterruptedOperation(operation.state) &&
    (operation.actor_id == null || sameActorId(operation.actor_id, actorId));
  const sheetRequired = sheetSelectionRequired(inspect);
  const openHeaders = unresolvedHeaders(inspect?.headers, mapping);
  const requirements = preview?.mapping_requirements;
  const requirementsTruncated = requirementsClaimBlocked(requirements);
  const zeroRowResult = isZeroRowCompleted(operation);

  const clearProtected = useCallback(() => {
    setInspect(null);
    setPreview(null);
    setPreviewFingerprint("");
    setOperation(null);
    setRowPage(null);
    setMembers([]);
    setMapping({});
    setValueMappings({});
    setResponsibleMapping({});
    setMappingConfirmed(false);
    setCommitAttempt(null);
    setReceiptArm(null);
    setReceiptGeneration(0);
    setResumeMode(null);
    commitKeyRef.current = null;
    pinnedResumeKeyRef.current = null;
    fetchedReceiptRef.current = null;
  }, []);

  const applyRowFailure = useCallback((rowErr, op) => {
    if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
    const view = errorView(rowErr);
    if (view.status === 403 || view.status === 404) {
      clearProtected();
      setState(view.status === 404 ? "not-found" : "denied");
      return;
    }
    setRowPage(null);
    setError(textError(ROW_RESULTS_UNAVAILABLE));
  }, [clearProtected, scopeGate]);

  const loadOperation = useCallback(
    async (op, id) => {
      const payload = await fetchClientImportOperation(companyId, id);
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
      setOperation(payload);
      setStep(5);
      setState("ready");
      try {
        const rows = await fetchClientImportOperationRows(companyId, id, {
          page: 1,
          page_size: ROW_PAGE_SIZE,
        });
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
        setRowPage(rows);
      } catch (rowErr) {
        applyRowFailure(rowErr, op);
      }
    },
    [applyRowFailure, companyId, scopeGate],
  );

  const loadAuthority = useCallback(async ({ background = false } = {}) => {
    if (!companyId) {
      setState("not-found");
      return;
    }
    const op = scopeGate.beginRead({ companyId, actorId });
    if (!background) {
      setState("loading");
      setError(null);
    }
    try {
      const [companyData, mine] = await Promise.all([
        fetchCompany(companyId),
        fetchMyCompany(),
      ]);
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
      const mineCompanyMatches =
        mine?.company?.id != null && sameCompanyId(mine.company.id, companyId);
      const mineMatches = mineCompanyMatches ? mine.membership : null;
      setCompany(companyData);
      setMembership(mineMatches);
      setAuthorityActorId(actorId);
      const allowed =
        mineCompanyMatches &&
        isAdminMembership(companyData, mineMatches) &&
        isCorporateClientsAvailable(companyData);
      if (!allowed) {
        clearProtected();
        setState("denied");
        return;
      }
      const id = readOperationId(location.search);
      if (id) {
        await loadOperation(op, id);
        return;
      }
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
      setState("ready");
    } catch (err) {
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
      clearProtected();
      const view = errorView(err);
      if (view.status === 404) setState("not-found");
      else if (view.status === 403) setState("denied");
      else {
        setError(presentApiError(view));
        setState("error");
      }
    }
  }, [actorId, clearProtected, companyId, loadOperation, location.search, scopeGate]);

  useEffect(() => {
    mountedRef.current = true;
    scopeGate.bindIdentity({ companyId, actorId });
    busyRef.current = false;
    setCompany(null);
    setMembership(null);
    setAuthorityActorId(null);
    setFile(null);
    setFileToken("");
    setSheet("");
    setSheetExplicit(false);
    setSourceNamespace("");
    setResumeMode(null);
    setStep(1);
    fileSelectionRef.current += 1;
    pinnedResumeKeyRef.current = null;
    clearProtected();
    loadAuthority();
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
      busyRef.current = false;
    };
  }, [companyId, actorId]); // eslint-disable-line react-hooks/exhaustive-deps

  useCompanyFocusRevalidation({
    enabled: state === "ready" && canImport,
    busy,
    onRevalidate: () => loadAuthority({ background: true }),
  });

  useLayoutEffect(() => {
    const list = stepsRef.current;
    if (!list) return undefined;
    const align = () => {
      const current = list.querySelector('[aria-current="step"]');
      if (!current) return;
      const listRect = list.getBoundingClientRect();
      const itemRect = current.getBoundingClientRect();
      if (itemRect.left < listRect.left - 1) {
        list.scrollLeft -= listRect.left - itemRect.left;
      } else if (itemRect.right > listRect.right + 1) {
        list.scrollLeft += itemRect.right - listRect.right;
      }
    };
    align();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(align);
    observer.observe(list);
    return () => observer.disconnect();
  }, [step, state]);

  useEffect(() => {
    if (!canImport || !mapping.responsible_user) {
      setMembers([]);
      return undefined;
    }
    const op = scopeGate.beginRead({ companyId, actorId });
    let cancelled = false;
    fetchMembers(companyId, "active")
      .then((payload) => {
        if (cancelled || !scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        setMembers(activeMemberOptions(payload));
      })
      .catch(() => {
        if (!cancelled && scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          setMembers([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [actorId, canImport, companyId, mapping.responsible_user, scopeGate]);

  useEffect(() => {
    if (!receiptGeneration || receiptArm == null || operationId !== receiptArm) {
      return undefined;
    }
    if (fetchedReceiptRef.current === receiptGeneration) return undefined;
    fetchedReceiptRef.current = receiptGeneration;
    const id = receiptArm;
    const op = scopeGate.beginRead({ companyId, actorId });
    fetchClientImportOperationRows(companyId, id, {
      page: 1,
      page_size: ROW_PAGE_SIZE,
    })
      .then((rows) => {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
        setRowPage(rows);
        setError(null);
      })
      .catch((rowErr) => {
        applyRowFailure(rowErr, op);
      });
    return undefined;
  }, [
    actorId,
    applyRowFailure,
    companyId,
    operationId,
    receiptArm,
    receiptGeneration,
    scopeGate,
  ]);

  function invalidatePreview() {
    setMappingConfirmed(false);
    setPreview(null);
    setPreviewFingerprint("");
    commitKeyRef.current = null;
  }

  function selectionIsCurrent(generation) {
    return mountedRef.current && fileSelectionRef.current === generation;
  }

  async function onSelectFile(nextFile) {
    const generation = fileSelectionRef.current + 1;
    fileSelectionRef.current = generation;
    setError(null);
    setCommitAttempt(null);
    setFile(nextFile);
    setInspect(null);
    setPreview(null);
    setPreviewFingerprint("");
    setMapping({});
    setMappingConfirmed(false);
    setValueMappings({});
    setResponsibleMapping({});
    setSheet("");
    setSheetExplicit(false);
    setOperation(null);
    setRowPage(null);
    setReceiptArm(null);
    setReceiptGeneration(0);
    commitKeyRef.current = null;
    fetchedReceiptRef.current = null;
    if (!nextFile) {
      setFileToken("");
      return;
    }
    const token = await fileContentToken(nextFile);
    if (!selectionIsCurrent(generation)) return;
    setFileToken(token);
    await runInspect(nextFile, "", generation);
  }

  async function runInspect(nextFile, nextSheet, generation) {
    const op = scopeGate.beginRead({ companyId, actorId });
    setBusy(true);
    busyRef.current = true;
    setError(null);
    try {
      const payload = await inspectClientImport(companyId, {
        file: nextFile,
        sheet: nextSheet || undefined,
      });
      if (!selectionIsCurrent(generation)) return;
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
      setInspect(payload);
      setStep(1);
    } catch (err) {
      if (!selectionIsCurrent(generation)) return;
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
      const view = errorView(err);
      if (view.status === 403 || view.status === 404) {
        clearProtected();
        setState(view.status === 404 ? "not-found" : "denied");
        return;
      }
      setError(presentApiError(view));
    } finally {
      if (selectionIsCurrent(generation)) {
        setBusy(false);
        busyRef.current = false;
      }
    }
  }

  async function onSheetChange(nextSheet) {
    const generation = fileSelectionRef.current;
    if (!nextSheet) {
      setSheet("");
      setSheetExplicit(false);
      invalidatePreview();
      return;
    }
    setSheet(nextSheet);
    setSheetExplicit(true);
    invalidatePreview();
    if (file) await runInspect(file, nextSheet, generation);
  }

  function applySuggestions() {
    const suggested = explicitMappingFromSuggestions(inspect?.header_suggestions);
    setMapping(suggested);
    invalidatePreview();
  }

  function updateMapping(key, header) {
    setMapping((current) => {
      const next = { ...current };
      if (!header) delete next[key];
      else next[key] = header;
      return next;
    });
    invalidatePreview();
  }

  async function runPreview() {
    if (!file || busyRef.current) return null;
    const missing = missingRequiredMappings(fields, mapping);
    if (missing.length) {
      setError(textError(missingMappingText(fields, missing)));
      return null;
    }
    if (sourceNamespaceMissing(mapping, sourceNamespace)) {
      setError(textError(NAMESPACE_REQUIRED_COPY));
      return null;
    }
    const op = scopeGate.beginRead({ companyId, actorId });
    setBusy(true);
    busyRef.current = true;
    setError(null);
    const requestFingerprint = importRequestFingerprint({
      fileToken,
      sheet,
      mapping,
      valueMappings,
      responsibleMapping,
      sourceNamespace,
    });
    try {
      const payload = await previewClientImport(companyId, {
        file,
        sheet: sheet || undefined,
        mapping,
        valueMappings,
        responsibleMapping: integerUserMap(responsibleMapping),
        sourceNamespace,
      });
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return null;
      setPreview(payload);
      setPreviewFingerprint(requestFingerprint);
      setCommitAttempt(null);
      return payload;
    } catch (err) {
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return null;
      const view = errorView(err);
      if (view.status === 403 || view.status === 404) {
        clearProtected();
        setState(view.status === 404 ? "not-found" : "denied");
        return null;
      }
      setError(presentApiError(view));
      return null;
    } finally {
      if (mountedRef.current) {
        setBusy(false);
        busyRef.current = false;
      }
    }
  }

  async function confirmMapping() {
    const missing = missingRequiredMappings(fields, mapping);
    if (missing.length) {
      setError(textError(missingMappingText(fields, missing)));
      return;
    }
    setMappingConfirmed(true);
    setError(null);
    setStep(3);
    await runPreview();
  }

  function updateValueMapping(field, token, canonical) {
    setValueMappings((current) => {
      const nextField = { ...(current[field] || {}) };
      if (!canonical) delete nextField[token];
      else nextField[token] = canonical;
      const next = { ...current };
      if (Object.keys(nextField).length) next[field] = nextField;
      else delete next[field];
      return next;
    });
    setPreviewFingerprint("");
    commitKeyRef.current = null;
  }

  function updateResponsible(token, userId) {
    setResponsibleMapping((current) => {
      const next = { ...current };
      if (!userId) delete next[token];
      else next[token] = Number(userId);
      return next;
    });
    setPreviewFingerprint("");
    commitKeyRef.current = null;
  }

  function beginReloadedResume() {
    if (
      !operation ||
      !isInterruptedOperation(operation.state) ||
      !sameActorId(operation.actor_id, actorId) ||
      !operation.idempotency_key
    ) {
      return;
    }
    pinnedResumeKeyRef.current = operation.idempotency_key;
    setResumeMode("reload");
    setError(null);
    setCommitAttempt(null);
    setFile(null);
    setFileToken("");
    setInspect(null);
    setPreview(null);
    setPreviewFingerprint("");
    setMapping({});
    setMappingConfirmed(false);
    setValueMappings({});
    setResponsibleMapping({});
    setSourceNamespace("");
    setSheet("");
    setSheetExplicit(false);
    setRowPage(null);
    setReceiptArm(null);
    setReceiptGeneration(0);
    setOperation(null);
    commitKeyRef.current = null;
    fetchedReceiptRef.current = null;
    setStep(1);
    navigate(
      { pathname: `/companies/${companyId}/clients/import` },
      { replace: true },
    );
  }

  async function commit() {
    if (busyRef.current || !file || !previewMatches) return;
    const op = scopeGate.beginRead({ companyId, actorId });
    const keyState = pinnedResumeKeyRef.current
      ? { key: pinnedResumeKeyRef.current, fingerprint }
      : resolveCommitKey({
          previous: commitKeyRef.current,
          fingerprint,
          createKey: createIdempotencyKey,
        });
    commitKeyRef.current = keyState;
    setBusy(true);
    busyRef.current = true;
    setError(null);
    setCommitAttempt(null);
    try {
      const payload = await commitClientImport(companyId, {
        file,
        sheet: sheet || undefined,
        mapping,
        valueMappings,
        responsibleMapping: integerUserMap(responsibleMapping),
        sourceNamespace,
        idempotencyKey: keyState.key,
      });
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
      const durableId = durableOperationId(payload);
      if (durableId == null) {
        setCommitAttempt("failed");
        setError(textError("Відповідь коміту не містить ідентифікатора операції."));
        return;
      }
      setOperation({ ...payload, id: durableId });
      setCommitAttempt("settled");
      if (isInterruptedOperation(payload.state)) {
        pinnedResumeKeyRef.current = keyState.key;
        setResumeMode("session");
      } else {
        pinnedResumeKeyRef.current = null;
        setResumeMode(null);
      }
      setStep(5);
      setReceiptArm(durableId);
      navigate(
        {
          pathname: `/companies/${companyId}/clients/import`,
          search: `?operation=${durableId}`,
        },
        { replace: true },
      );
      setReceiptGeneration((current) => current + 1);
    } catch (err) {
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
      const view = errorView(err);
      if (view.status === 409 && view.code === "idempotency_conflict") {
        setCommitAttempt("conflict");
        setError(presentApiError(view));
        return;
      }
      if (view.status === 403 || view.status === 404) {
        clearProtected();
        setState(view.status === 404 ? "not-found" : "denied");
        return;
      }
      if (view.status == null || view.status >= 500) {
        setCommitAttempt(view.status == null ? "uncertain" : "failed");
        setError(
          view.status == null
            ? textError(
                "Відповідь не отримано. Той самий ключ ідемпотентності збережено для повтору.",
              )
            : presentApiError(view),
        );
        return;
      }
      setCommitAttempt("failed");
      setError(presentApiError(view));
    } finally {
      if (mountedRef.current) {
        setBusy(false);
        busyRef.current = false;
      }
    }
  }

  function exitToNewImport() {
    fileSelectionRef.current += 1;
    pinnedResumeKeyRef.current = null;
    setResumeMode(null);
    setOperation(null);
    setRowPage(null);
    setFile(null);
    setFileToken("");
    setInspect(null);
    setPreview(null);
    setPreviewFingerprint("");
    setMapping({});
    setMappingConfirmed(false);
    setValueMappings({});
    setResponsibleMapping({});
    setSourceNamespace("");
    setSheet("");
    setSheetExplicit(false);
    setCommitAttempt(null);
    setError(null);
    setReceiptArm(null);
    setReceiptGeneration(0);
    commitKeyRef.current = null;
    fetchedReceiptRef.current = null;
    setFileInputKey((current) => current + 1);
    setStep(1);
    navigate(
      { pathname: `/companies/${companyId}/clients/import` },
      { replace: true },
    );
  }

  async function changeRowPage(nextPage) {
    if (!operation || busyRef.current) return;
    const op = scopeGate.beginRead({ companyId, actorId });
    setBusy(true);
    busyRef.current = true;
    try {
      const rows = await fetchClientImportOperationRows(companyId, operation.id, {
        page: nextPage,
        page_size: ROW_PAGE_SIZE,
      });
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
      setRowPage(rows);
    } catch (err) {
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) return;
      const view = errorView(err);
      if (view.status === 403 || view.status === 404) {
        clearProtected();
        setState(view.status === 404 ? "not-found" : "denied");
        return;
      }
      setError(presentApiError(view));
    } finally {
      if (mountedRef.current) {
        setBusy(false);
        busyRef.current = false;
      }
    }
  }

  const stepTitle = [
    "Файл",
    "Колонки",
    "Значення",
    "Попередній перегляд",
    "Результат",
  ];

  return (
    <section
      className="clients-page feature-company-workspace feature-company-clients feature-company-client-import"
      id="Csection"
      tabIndex="-1"
      data-step={step}
      aria-busy={busy ? "true" : "false"}
    >
      <div className="container">
        <CompanyPageHeader
          title="Імпорт клієнтів"
          subtitle="Клієнти компанії · Імпорт"
          company={authorityMatches ? company : null}
          membership={authorityMatches ? membership : null}
          companyId={companyId}
          activeNav="clients"
          actions={
            canImport ? (
              <Link
                to={`/companies/${companyId}/clients`}
                className="action-btn action-btn--ghost"
              >
                До списку
              </Link>
            ) : null
          }
        />

        {state === "loading" ? (
          <div className="feature-company-workspace__state">
            <Spinner />
          </div>
        ) : null}

        {state === "denied" ? (
          <EmptyState
            title="Немає доступу"
            description="Імпорт доступний лише активному власнику або керівнику компанії, коли запис клієнтів увімкнено."
          />
        ) : null}

        {state === "not-found" ? (
          <EmptyState
            title="Не знайдено"
            description="Компанію або операцію імпорту не знайдено."
          />
        ) : null}

        {state === "error" ? (
          <EmptyState
            title="Помилка завантаження"
            description={(error && error.primary) || "Спробуйте оновити сторінку."}
            primaryAction={
              <button type="button" className="action-btn" onClick={loadAuthority}>
                Повторити
              </button>
            }
          />
        ) : null}

        {state === "ready" && canImport ? (
          <div className="clients-workspace">
            <ol
              ref={stepsRef}
              className="feature-company-client-import__steps"
              aria-label="Кроки імпорту"
            >
              {stepTitle.map((label, index) => (
                <li
                  key={label}
                  aria-current={step === index + 1 ? "step" : undefined}
                >
                  {index + 1}. {label}
                </li>
              ))}
            </ol>

            {error ? (
              <div className="feature-company-client-import__error">
                <InlineError>{error.primary}</InlineError>
              </div>
            ) : null}

            {resumeMode === "reload" ? (
              <button
                type="button"
                className="action-btn action-btn--ghost"
                disabled={busy}
                onClick={exitToNewImport}
              >
                Новий імпорт
              </button>
            ) : null}

            {step === 1 ? (
              <div className="feature-company-client-import__panel">
                <div className="feature-company-client-import__file">
                  <label className="ui-field__label" htmlFor="client-import-file">
                    Файл CSV або XLSX
                  </label>
                  <input
                    key={fileInputKey}
                    ref={fileInputRef}
                    id="client-import-file"
                    className="feature-company-client-import__file-input"
                    type="file"
                    accept=".csv,.xlsx,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    aria-label="Файл CSV або XLSX"
                    disabled={busy}
                    onChange={(event) => {
                      const next = event.target.files && event.target.files[0];
                      onSelectFile(next || null);
                    }}
                  />
                  <div className="feature-company-client-import__file-actions">
                    <button
                      type="button"
                      className="action-btn action-btn--ghost"
                      disabled={busy}
                      onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    >
                      {file ? "Замінити файл" : "Обрати файл"}
                    </button>
                  </div>
                  <p className="feature-company-workspace__meta" role="status">
                    {file ? file.name : "Файл не обрано"}
                  </p>
                </div>
                <p className="feature-company-workspace__meta">
                  Файл читає сервер. Браузер не розбирає XLSX самостійно.
                  Формули не виконуються.
                </p>
                {resumeMode === "reload" ? (
                  <p role="status">{RELOAD_RESUME_COPY}</p>
                ) : null}
                {inspect?.bounds ? (
                  <p className="feature-company-workspace__meta">
                    Обмеження сервера: {inspect.bounds.max_upload_bytes} байт,{" "}
                    {inspect.bounds.max_data_rows} рядків, {inspect.bounds.max_columns}{" "}
                    колонок, {inspect.bounds.max_cell_length} символів у комірці,{" "}
                    {inspect.bounds.max_sheets} аркушів.
                  </p>
                ) : null}
                {inspect ? (
                  <div>
                    <p>
                      Формат: {formatLabel(inspect.format)}. Рядків: {inspect.row_count}. Колонок:{" "}
                      {(inspect.headers || []).length}.
                    </p>
                    {(inspect.warnings || []).map((warning) => {
                      const presented = inspectWarningText(warning);
                      return (
                        <p
                          key={`${warning.code}-${warning.detail || ""}`}
                          className="feature-company-client-import__status-block"
                          role="status"
                        >
                          <span>{presented.primary}</span>
                        </p>
                      );
                    })}
                    {sheetRequired ? (
                      <Field id="client-import-sheet" label="Аркуш">
                        <select
                          className="ui-select"
                          aria-label="Аркуш"
                          value={sheetExplicit ? sheet : ""}
                          disabled={busy}
                          onChange={(event) => onSheetChange(event.target.value)}
                        >
                          <option value="">Оберіть аркуш</option>
                          {(inspect.sheets || []).map((item) => (
                            <option key={item.name} value={item.name}>
                              {item.name}
                            </option>
                          ))}
                        </select>
                      </Field>
                    ) : null}
                    {sheetRequired && !sheetExplicit ? (
                      <p role="status">Оберіть аркуш явно. Перший аркуш не підтверджено.</p>
                    ) : null}
                    <button
                      type="button"
                      className="action-btn"
                      disabled={busy || (sheetRequired && !sheetExplicit)}
                      onClick={() => setStep(2)}
                    >
                      Далі
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}

            {step === 2 ? (
              <div className="feature-company-client-import__panel">
                <p>
                  Пропозиції сервера не є зіставленням. Підтвердіть колонки явно.
                </p>
                <button
                  type="button"
                  className="action-btn action-btn--ghost"
                  disabled={busy}
                  onClick={applySuggestions}
                >
                  Застосувати однозначні пропозиції
                </button>
                <div className="feature-company-client-import__map-list">
                  {fields.map((field) => (
                    <Field
                      key={field.key}
                      id={`client-import-map-${field.key}`}
                      className="feature-company-client-import__map-row"
                      label={`${importFieldLabel(field)}${field.required_mapping ? " (обов'язково)" : ""}`}
                      help={fieldKindLabel(field.kind) || undefined}
                    >
                      <select
                        className="ui-select"
                        aria-label={`Колонка для ${importFieldLabel(field)}`}
                        value={mapping[field.key] || ""}
                        disabled={busy}
                        onChange={(event) => updateMapping(field.key, event.target.value)}
                      >
                        <option value="">Не зіставлено</option>
                        {(inspect?.headers || []).map((header) => (
                          <option key={header} value={header}>
                            {header}
                          </option>
                        ))}
                      </select>
                    </Field>
                  ))}
                </div>
                <div aria-label="Незіставлені колонки">
                  {openHeaders.length ? (
                    <ul>
                      {openHeaders.map((header) => {
                        const suggestion = (inspect?.header_suggestions || []).find(
                          (item) => item.header === header,
                        );
                        return (
                          <li key={header}>
                            {header} —{" "}
                            {suggestion?.status === "ambiguous"
                              ? "неоднозначно"
                              : "не зіставлено"}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p>Усі колонки файлу зіставлено.</p>
                  )}
                </div>
                <button
                  type="button"
                  className="action-btn action-btn--ghost"
                  disabled={busy}
                  onClick={() => setStep(1)}
                >
                  Назад
                </button>
                <button
                  type="button"
                  className="action-btn"
                  disabled={busy}
                  onClick={confirmMapping}
                >
                  Підтвердити зіставлення
                </button>
              </div>
            ) : null}

            {step === 3 && mappingConfirmed ? (
              <div className="feature-company-client-import__panel">
                <p>
                  Канонічні значення беруться з метаданих сервера. Незіставлене
                  значення блокує лише свій рядок. Переклад джерела не вгадується.
                </p>
                <p className="feature-company-workspace__meta">
                  Якщо колонку відповідального не зіставлено, відповідальним стає
                  той, хто імпортує. Порожня комірка залишає клієнта без
                  відповідального. Незіставлене або недійсне значення блокує рядок.
                </p>
                {requirementsTruncated ? (
                  <p role="status">
                    Список неповний. Не можна вважати, що всі значення зіставлено.
                  </p>
                ) : null}
                {(requirements?.fields || []).length === 0 && previewMatches && !requirementsTruncated ? (
                  <p>Незіставлених значень немає.</p>
                ) : null}
                {(requirements?.fields || []).map((requirement) => (
                  <fieldset key={requirement.field}>
                    <legend>
                      {importFieldLabel({ key: requirement.field })}
                      {fieldKindLabel(requirement.kind)
                        ? ` · ${fieldKindLabel(requirement.kind)}`
                        : ""}
                      {requirement.truncated
                        ? ` · пропущено ${requirement.omitted_count}`
                        : ""}
                    </legend>
                    {(requirement.source_values || []).map((token, index) =>
                      requirement.kind === "responsible" ? (
                        <Field
                          key={token}
                          id={`client-import-responsible-${requirement.field}-${index}`}
                          className="feature-company-client-import__map-row"
                          label={token}
                        >
                          <select
                            className="ui-select"
                            aria-label={`Відповідальний для ${token}`}
                            value={responsibleMapping[token] || ""}
                            disabled={busy}
                            onChange={(event) =>
                              updateResponsible(token, event.target.value)
                            }
                          >
                            <option value="">Не зіставлено</option>
                            {members.map((member) => (
                              <option key={member.userId} value={member.userId}>
                                {member.label}
                              </option>
                            ))}
                          </select>
                        </Field>
                      ) : (
                        <Field
                          key={token}
                          id={`client-import-value-${requirement.field}-${index}`}
                          className="feature-company-client-import__map-row"
                          label={token}
                        >
                          <select
                            className="ui-select"
                            aria-label={`Канонічне значення для ${importFieldLabel({ key: requirement.field })} ${token}`}
                            value={
                              (valueMappings[requirement.field] || {})[token] || ""
                            }
                            disabled={busy}
                            onChange={(event) =>
                              updateValueMapping(
                                requirement.field,
                                token,
                                event.target.value,
                              )
                            }
                          >
                            <option value="">Не зіставлено</option>
                            {allowedValuesFor(fields, requirement.field).map((value) => (
                              <option key={value} value={value}>
                                {canonicalChoiceText(value, requirement.field)}
                              </option>
                            ))}
                          </select>
                        </Field>
                      ),
                    )}
                  </fieldset>
                ))}
                {namespaceNeeded ? (
                  <Field
                    id="client-import-namespace"
                    label="Простір джерела"
                    help="Простір джерела ідентифікує зовнішню систему, а не ім'я файлу. Довжину і нормалізацію визначає сервер. Без нього рядки із зовнішнім ідентифікатором залишаються заблокованими. Існуюче джерело не оновлює клієнта і не зливає записи."
                  >
                    <TextInput
                      aria-label="Простір джерела"
                      value={sourceNamespace}
                      disabled={busy}
                      onChange={(event) => {
                        setSourceNamespace(event.target.value);
                        setPreviewFingerprint("");
                        commitKeyRef.current = null;
                      }}
                    />
                  </Field>
                ) : null}
                {namespaceMissing ? (
                  <p role="alert">{NAMESPACE_REQUIRED_COPY}</p>
                ) : null}
                <button
                  type="button"
                  className="action-btn action-btn--ghost"
                  disabled={busy}
                  onClick={() => setStep(2)}
                >
                  Назад
                </button>
                <button
                  type="button"
                  className="action-btn"
                  disabled={busy || namespaceMissing}
                  onClick={async () => {
                    const payload = await runPreview();
                    if (payload) setStep(4);
                  }}
                >
                  Попередній перегляд
                </button>
              </div>
            ) : null}

            {step === 4 ? (
              <div className="feature-company-client-import__panel">
                {!previewMatches ? (
                  <p role="status">
                    Вхідні дані змінено. Потрібен новий попередній перегляд перед імпортом.
                  </p>
                ) : (
                  <>
                    <p>
                      Готово: {preview.summary.ready}. Готово з попередженням:{" "}
                      {preview.summary.warning}. Заблоковано: {preview.summary.blocked}.
                      Помилка: {preview.summary.error}.
                      {preview.summary.truncated
                        ? " Показано не всі рядки файлу."
                        : ""}
                    </p>
                    <p>
                      Буде спроба створення: {counts.attempted}. Не створюється:{" "}
                      {counts.blocked}.
                    </p>
                    {Number(preview.summary.row_count) === 0 ? (
                      <p role="status">
                        Рядків даних немає. Імпорт буде завершено без створення клієнтів.
                      </p>
                    ) : null}
                    {requirementsTruncated ? (
                      <p role="status">
                        Список неповний. Не можна вважати, що всі значення зіставлено.
                      </p>
                    ) : null}
                    <PreviewRows rows={preview.rows || []} />
                  </>
                )}
                <button
                  type="button"
                  className="action-btn action-btn--ghost"
                  disabled={busy}
                  onClick={() => setStep(3)}
                >
                  Назад
                </button>
                <button
                  type="button"
                  className="action-btn"
                  disabled={busy || !previewMatches}
                  onClick={commit}
                >
                  {busy ? "Імпорт виконується" : "Імпортувати"}
                </button>
                {commitAttempt === "uncertain" ? (
                  <button
                    type="button"
                    className="action-btn"
                    disabled={busy}
                    onClick={commit}
                  >
                    Повторити той самий запит
                  </button>
                ) : null}
              </div>
            ) : null}

            {step === 5 && operation ? (
              <div className="feature-company-client-import__panel">
                <div className="feature-company-client-import__status-block">
                  <PresentedStatus
                    label={operationStateLabel(operation.state)}
                    code={operation.state}
                    tone={operationStateTone(operation.state)}
                  />
                </div>
                {zeroRowResult ? (
                  <p role="status">
                    Імпорт завершено без рядків даних. Створено 0, заблоковано 0,
                    існуюче джерело 0, помилки 0.
                  </p>
                ) : null}
                {isInterruptedOperation(operation.state) ? (
                  <p role="status">{INTERRUPTED_COPY}</p>
                ) : null}
                {isInterruptedOperation(operation.state) && !actorCanResume ? (
                  <p role="status">{CROSS_ACTOR_RESUME_COPY}</p>
                ) : null}
                {actorCanResume && resumeMode !== "session" ? (
                  <p role="status">{RELOAD_RESUME_COPY}</p>
                ) : null}
                {operation.state === "failed" ? (
                  <div className="feature-company-client-import__error">
                    <InlineError>
                      Збій операції. Рядки не позначено як успішно імпортовані.
                    </InlineError>
                    {operation.failure_code ? (
                      <p className="feature-company-workspace__meta">
                        {diagnosticCodeText(operation.failure_code).primary}
                      </p>
                    ) : null}
                  </div>
                ) : null}
                {operation.summary && !zeroRowResult ? (
                  <p>
                    Рядків: {operation.summary.row_count}. Створено:{" "}
                    {operation.summary.created}. Заблоковано: {operation.summary.blocked}.
                    Існуюче джерело: {operation.summary.existing_source}. Помилки рядків:{" "}
                    {operation.summary.failed}.
                  </p>
                ) : null}
                <ResultRows companyId={companyId} page={rowPage} />
                {rowPage ? (
                  <NumberedPagination
                    currentPage={rowPage.page}
                    totalPages={Math.max(
                      1,
                      Math.ceil((rowPage.count || 0) / (rowPage.page_size || ROW_PAGE_SIZE)),
                    )}
                    hasNext={
                      rowPage.page * (rowPage.page_size || ROW_PAGE_SIZE) <
                      (rowPage.count || 0)
                    }
                    hasPrevious={rowPage.page > 1}
                    ariaLabel="Сторінки результатів імпорту"
                    disabled={busy}
                    onPageChange={changeRowPage}
                  />
                ) : null}
                {!operationId ? null : (
                  <p className="feature-company-workspace__meta">
                    Операція {operation.id} збережена на цій сторінці.
                  </p>
                )}
                {sameSessionContinuation ? (
                  <button
                    type="button"
                    className="action-btn"
                    disabled={busy || !previewMatches}
                    onClick={commit}
                  >
                    Продовжити цей імпорт
                  </button>
                ) : null}
                {actorCanResume && resumeMode !== "session" ? (
                  <button
                    type="button"
                    className="action-btn"
                    disabled={busy}
                    onClick={beginReloadedResume}
                  >
                    Відновити імпорт
                  </button>
                ) : null}
                <button
                  type="button"
                  className="action-btn action-btn--ghost"
                  disabled={busy}
                  onClick={exitToNewImport}
                >
                  Новий імпорт
                </button>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function PresentedStatus({ label, tone }) {
  return (
    <span className="feature-company-client-import__status">
      <span className={`feature-company-client-import__pill feature-company-client-import__pill--${tone || "neutral"}`}>
        {label}
      </span>
    </span>
  );
}

function PreviewRows({ rows }) {
  return (
    <div className="clients-table-wrap" role="region" aria-label="Рядки попереднього перегляду">
      <table className="clients-table">
        <thead>
          <tr>
            <th scope="col">Рядок</th>
            <th scope="col">Класифікація</th>
            <th scope="col">Діагностика</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.source_row_number}>
              <td data-label="Рядок">{row.source_row_number}</td>
              <td data-label="Класифікація">
                <PresentedStatus
                  label={classificationLabel(row.classification)}
                  code={row.classification}
                  tone={classificationTone(row.classification)}
                />
              </td>
              <td data-label="Діагностика">
                {(row.diagnostics || []).length === 0
                  ? "—"
                  : (row.diagnostics || []).map((item) => {
                      const presented = diagnosticText(item);
                      return (
                        <span
                          key={`${item.code}-${item.field || ""}-${item.message}`}
                          className="feature-company-client-import__status"
                        >
                          <span>{presented.primary}</span>
                        </span>
                      );
                    })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ResultRows({ companyId, page }) {
  const rows = page?.results || [];
  return (
    <div className="clients-table-wrap" role="region" aria-label="Результати імпорту">
      <table className="clients-table">
        <thead>
          <tr>
            <th scope="col">Рядок</th>
            <th scope="col">Результат</th>
            <th scope="col">Діагностика</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.source_row_number}>
              <td data-label="Рядок">{row.source_row_number}</td>
              <td data-label="Результат">
                <PresentedStatus
                  label={outcomeLabel(row.outcome)}
                  code={row.outcome}
                  tone={outcomeTone(row.outcome)}
                />
                {row.client_id ? (
                  <>
                    {" "}
                    <Link to={`/companies/${companyId}/clients/${row.client_id}`}>
                      Відкрити клієнта
                    </Link>
                  </>
                ) : null}
                {row.outcome === "existing_source" ? (
                  <span> Оновлення не виконано і дублікат не створено.</span>
                ) : null}
              </td>
              <td data-label="Діагностика">
                {(row.diagnostic_codes || []).length
                  ? (row.diagnostic_codes || []).map((code) => {
                      const presented = diagnosticCodeText(code);
                      return (
                        <span key={code} className="feature-company-client-import__status">
                          <span>{presented.primary}</span>
                        </span>
                      );
                    })
                  : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default CompanyClientImportContent;
